import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Activity, Cable, ShieldAlert, Loader2, Layers,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { getWebSocketClient } from '../../utils/websocket-client';
import { ioApi, type IoClientModule } from '../../utils/ioModules';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface Waveshare8CHDashboardProps {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
  /** Admin → IO Modules: shows the animated module drawings. On unless switched off. */
  showPanelDrawing?: boolean;
}

const SLOTS = Array.from({ length: 8 }, (_, i) => i + 1);

const DI_COLOR = '#10b981'; // emerald
const HISTORY_KEEP_MS = 5 * 60_000;

type Transition = { t: number; v: boolean };
/** One visible input in the current panel (inputs from several modules are merged). */
type PanelChannel = { key: string; moduleId: string; moduleName: string; ch: number; name: string };

const ago = (ts?: number) => {
  if (!ts) return 'no change yet';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 2) return 'changed just now';
  if (s < 60) return `changed ${s}s ago`;
  if (s < 3600) return `changed ${Math.floor(s / 60)}m ago`;
  return `changed ${new Date(ts).toLocaleTimeString()}`;
};

// ──────────────────────────────────────────────────
// Animated pieces
// ──────────────────────────────────────────────────

function Led({ on, color, size = 12, label }: { on: boolean; color: string; size?: number; label?: string }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }} aria-label={label}>
      <motion.span
        className="absolute inset-0 rounded-full"
        animate={{
          backgroundColor: on ? color : 'rgba(127,127,127,0.28)',
          boxShadow: on ? `0 0 ${size}px ${color}, 0 0 ${size * 2}px ${color}66` : '0 0 0 transparent',
        }}
        transition={{ duration: 0.2 }}
      />
      {on && <span className="absolute inset-[22%] hidden rounded-full bg-white/70 dark:block" />}
    </span>
  );
}

/**
 * Drawing of one 8CH module (inputs only — the dashboard is read-only). Only inputs
 * shown to the client get an LED; hidden ones are blank slots.
 */
function ModuleHero({
  mod, di, connected, packetAt, compact,
}: { mod: IoClientModule; di: boolean[]; connected: boolean; packetAt?: number; compact?: boolean }) {
  const reduce = useReducedMotion();
  const diShown = new Set(mod.inputs.map((c) => c.ch));
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-gradient-to-b from-muted/60 to-muted/20 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 bg-foreground/5" aria-hidden />
      <div className="relative mx-auto max-w-xl rounded-lg border-2 border-foreground/15 bg-card p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="truncate text-[11px] font-bold">{mod.name}</div>
          <div className="flex shrink-0 items-center gap-3 text-[10px] font-semibold text-muted-foreground">
            <span className="flex items-center gap-1.5"><Led on={connected} color={DI_COLOR} size={8} label="Power" /> PWR</span>
            <span className="flex items-center gap-1.5">
              <span className="relative inline-flex h-2 w-2">
                {packetAt && connected && !reduce ? (
                  <motion.span key={packetAt} className="absolute inset-0 rounded-full" initial={{ backgroundColor: '#fab219', boxShadow: '0 0 10px #fab219' }} animate={{ backgroundColor: 'rgba(127,127,127,0.28)', boxShadow: '0 0 0 transparent' }} transition={{ duration: 0.7 }} />
                ) : (
                  <span className="absolute inset-0 rounded-full" style={{ backgroundColor: 'rgba(127,127,127,0.28)' }} />
                )}
              </span>
              RX
            </span>
          </div>
        </div>
        {[{ label: 'DI', states: di, shown: diShown, color: DI_COLOR }].map((row) => (
          <div key={row.label} className="mb-1.5 flex items-center gap-3 last:mb-0">
            <span className="w-6 text-[10px] font-bold text-muted-foreground">{row.label}</span>
            <div className="grid flex-1 grid-cols-8 gap-2">
              {SLOTS.map((ch) => (
                <div key={ch} className="flex flex-col items-center gap-1">
                  {row.shown.has(ch)
                    ? <Led on={!!row.states[ch - 1]} color={row.color} size={compact ? 12 : 14} label={`${row.label}${ch} ${row.states[ch - 1] ? 'on' : 'off'}`} />
                    : <span className="h-3 w-3 rounded-full border border-dashed border-foreground/15" aria-hidden />}
                  <span className="text-[9px] tabular-nums text-muted-foreground">{ch}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="relative mt-1.5 text-center text-[11px] text-muted-foreground">
        {mod.location || mod.modelName} · slave {mod.slaveId}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────
// Screen
// ──────────────────────────────────────────────────

export function Waveshare8CHDashboard({ clientId, token, pollingIntervalMs = 5000, showPanelDrawing = true }: Waveshare8CHDashboardProps) {
  const reduce = useReducedMotion();
  // Modules come from Admin → IO Modules; only channels marked "Show to client" arrive here.
  const [modules, setModules] = useState<IoClientModule[]>([]);
  const [panel, setPanel] = useState<string | null>(null);
  const [live, setLive] = useState<Record<string, { inputs: boolean[]; at: number }>>({});
  const [linkStatus, setLinkStatus] = useState<Record<string, { connected: boolean; error?: string | null; reconnecting?: boolean }>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'on' | 'off'>('all');
  const historyRef = useRef<Record<string, Transition[]>>({});
  const [, forceTick] = useState(0);

  // Re-render every second: keeps online badges, "changed Xs ago" and the timeline accurate.
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const loadModules = useCallback(async () => {
    try {
      const list = await ioApi.listForClient(token, clientId);
      setModules(list);
      const panels = Array.from(new Set(list.map((m) => m.panel)));
      setPanel((prev) => (prev && panels.includes(prev) ? prev : panels[0] ?? null));
    } catch {
      toast.error('Failed to load IO modules');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => { loadModules(); }, [loadModules]);

  useEffect(() => {
    let mounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });
    const handleLive = (data: any) => {
      if (!mounted || !data?.deviceId) return;
      const id = String(data.deviceId);
      const inputs: boolean[] = Array.isArray(data.inputs) ? data.inputs.map(Boolean) : [];
      const now = Date.now();
      const record = (key: string, v: boolean) => {
        const hist = historyRef.current[key] || [];
        const last = hist[hist.length - 1];
        if (!last || last.v !== v) {
          hist.push({ t: now, v });
          historyRef.current[key] = hist.filter((h) => h.t >= now - HISTORY_KEEP_MS);
        }
      };
      inputs.forEach((v, i) => record(`${id}:DI${i + 1}`, v));
      setLive((prev) => ({ ...prev, [id]: { inputs, at: now } }));
    };
    const handleStatus = (data: any) => {
      if (!mounted || !data?.deviceId) return;
      setLinkStatus((prev) => ({ ...prev, [data.deviceId]: { connected: !!data.connected && data.comm !== 'error', error: data.error, reconnecting: !!data.reconnecting } }));
    };
    ws.on('io_live_data', handleLive);
    ws.on('io_status_update', handleStatus);
    ws.connect();
    return () => {
      mounted = false;
      ws.off('io_live_data', handleLive);
      ws.off('io_status_update', handleStatus);
    };
  }, [clientId, token, pollingIntervalMs]);

  // ──────────────────────────────────────────────────
  // Derived state for the selected panel
  // ──────────────────────────────────────────────────
  const panels = Array.from(new Set(modules.map((m) => m.panel)));
  const panelModules = modules.filter((m) => m.panel === panel);
  const multi = panelModules.length > 1;
  const isOnline = (m: IoClientModule) => {
    const l = live[m.id];
    if (!l) return false;
    const age = Date.now() - l.at;
    // A lost reply or two on a busy RS-485 line is not an outage: while the module's
    // last reading is recent, keep it online even if the bridge reported a miss.
    const graceMs = Math.max(6_000, (m.pollIntervalMs || 1000) * 4);
    return age < Math.max(15_000, (m.pollIntervalMs || 1000) * 6) && (linkStatus[m.id]?.connected !== false || age < graceMs);
  };
  const inputChannels: PanelChannel[] = panelModules.flatMap((m) => m.inputs.map((c) => ({ key: `${m.id}:DI${c.ch}`, moduleId: m.id, moduleName: m.name, ch: c.ch, name: c.name })));

  const value = (c: PanelChannel) => !!live[c.moduleId]?.inputs[c.ch - 1];
  const known = (c: PanelChannel) => !!live[c.moduleId];
  const lastChangeAt = (key: string) => {
    const hist = historyRef.current[key];
    return hist && hist.length > 1 ? hist[hist.length - 1].t : undefined;
  };
  const sub = (c: PanelChannel) => (multi ? `${c.moduleName} · DI${c.ch}` : `DI${c.ch}`);

  const diStates = inputChannels.map(value);
  const activeInputs = diStates.filter(Boolean).length;
  const offInputs = inputChannels.filter((c) => known(c) && !value(c)).length;
  const matchesFilter = (c: PanelChannel) => filter === 'all' || (filter === 'on' ? value(c) : known(c) && !value(c));
  const onlineCount = panelModules.filter(isOnline).length;
  const offline = panelModules.filter((m) => !isOnline(m));
  const lastPacketAt = Math.max(0, ...panelModules.map((m) => live[m.id]?.at || 0)) || undefined;

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────
  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (modules.length === 0) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-6">
          <p className="font-semibold text-amber-500">No IO modules configured</p>
          <p className="text-sm text-muted-foreground">Your administrator adds Waveshare modules in Admin Panel → IO Modules (COM port, slave ID, channel names and which channels you can see).</p>
        </CardContent>
      </Card>
    );
  }

  const now = Date.now();
  const fadeUp = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] as any },
  });
  const allOnline = onlineCount === panelModules.length && panelModules.length > 0;

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <motion.section key={panel} {...fadeUp(0)} className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Cable className="h-4 w-4 text-emerald-500" /> Waveshare IO panel
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">{panel}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {panelModules.length} module{panelModules.length === 1 ? '' : 's'} · {inputChannels.length} input{inputChannels.length === 1 ? '' : 's'} · read-only
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {panels.length > 1 && (
              <div className="w-56">
                <Select value={panel ?? undefined} onValueChange={setPanel}>
                  <SelectTrigger><Layers className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Select panel" /></SelectTrigger>
                  <SelectContent>{panels.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold" title="Digital inputs currently ON">
              <Activity className="h-3.5 w-3.5 text-emerald-500" />
              <span className="tabular-nums">{activeInputs}</span><span className="font-medium text-muted-foreground">/ {inputChannels.length} inputs on</span>
            </div>
            <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${allOnline ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-amber-500/30 bg-amber-500/10'}`}
              title={lastPacketAt ? `Last update ${Math.max(0, Math.round((now - lastPacketAt) / 1000))}s ago` : 'Waiting for the bridge'}>
              <span className="relative flex h-2 w-2">
                {allOnline && (
                  <motion.span key={lastPacketAt} className="absolute inset-0 rounded-full bg-emerald-500" initial={{ scale: 1, opacity: 0.7 }} animate={{ scale: reduce ? 1 : 2.6, opacity: 0 }} transition={{ duration: 1 }} />
                )}
                <span className={`relative h-2 w-2 rounded-full ${allOnline ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              </span>
              <span className={allOnline ? 'text-emerald-500' : 'text-amber-500'}><span className="tabular-nums">{onlineCount}</span></span>
              <span className="font-medium text-muted-foreground">/ {panelModules.length} modules online</span>
            </div>
          </div>
        </div>

        {showPanelDrawing && (
          <div className={`mt-5 grid gap-4 ${multi ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
            {panelModules.map((m) => (
              <ModuleHero key={m.id} mod={m} di={live[m.id]?.inputs || []} connected={isOnline(m)} packetAt={live[m.id]?.at} compact={multi} />
            ))}
          </div>
        )}
      </motion.section>

      {/* ── Inputs: readable tiles, grouped by module ── */}
      {inputChannels.length > 0 && (
        <motion.div {...fadeUp(5)}>
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 px-5 pb-3 pt-5">
              <CardTitle className="text-lg">Digital inputs</CardTitle>
              <div className="flex rounded-lg border p-0.5" role="group" aria-label="Filter inputs">
                {([
                  ['all', 'All', inputChannels.length],
                  ['on', 'ON', activeInputs],
                  ['off', 'OFF', offInputs],
                ] as const).map(([id, label, count]) => (
                  <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${filter === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    {label} <span className="tabular-nums">{count}</span>
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="space-y-5 px-5 pb-5">
              {filter !== 'all' && inputChannels.every((c) => !matchesFilter(c)) && (
                <p className="py-6 text-center text-sm text-muted-foreground">No inputs are {filter === 'on' ? 'ON' : 'OFF'} right now.</p>
              )}
              {panelModules.map((m) => {
                const chans = inputChannels.filter((c) => c.moduleId === m.id && matchesFilter(c));
                if (chans.length === 0) return null;
                return (
                  <div key={m.id} className="space-y-2">
                    <div className="flex items-center gap-2 text-base font-bold">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${isOnline(m) ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      <span className="min-w-0 break-words">{m.name}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {chans.map((c) => {
                        const on = value(c);
                        return (
                          <div key={c.key} title={`${c.name} — ${sub(c)} · ${ago(lastChangeAt(c.key))}`}
                            className={`flex min-w-0 items-center gap-3 rounded-lg border p-3 transition-colors duration-300 ${on ? 'border-emerald-500/50 bg-emerald-500/10' : 'bg-muted/20'}`}>
                            <Led on={on} color={DI_COLOR} size={14} label={`${c.name} ${on ? 'on' : 'off'}`} />
                            <div className="min-w-0 flex-1">
                              <div className="break-words text-sm font-semibold leading-snug">{c.name}</div>
                              <div className="mt-0.5 text-xs text-muted-foreground">DI{c.ch}</div>
                            </div>
                            <span className={`shrink-0 rounded-md px-2.5 py-1 text-sm font-bold ${on ? 'bg-emerald-600 text-white' : 'bg-muted text-foreground'}`}>{known(c) ? (on ? 'ON' : 'OFF') : '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Modules not answering ── */}
      <AnimatePresence>
        {offline.length > 0 && (
          <motion.div key="offline" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="space-y-2 p-4">
                {offline.map((m) => (
                  <div key={m.id} className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div className="text-sm">
                      <span className="font-semibold text-amber-500">{m.name}: {linkStatus[m.id]?.reconnecting ? 'reconnecting…' : live[m.id] ? 'no fresh data' : 'waiting for the bridge'}</span>
                      <span className="text-muted-foreground"> — {linkStatus[m.id]?.error || 'the bridge keeps retrying automatically.'}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
